import "./RoomView.scss";

import React, { useRef } from "react";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

import Point from "../models/Point";
import Page, { mmToPixel } from "../models/Page";
import { PagePrintInfo } from "../models/PagePrintInfo";
import Logger from "../Logger";
import { useRedraw } from "../models/useRerender";
import { bringElementToTop, measureText, pointInBounderyRect } from "./GraficElementUtils";
import Room from "../models/Room";
import ConsumersView from "./ConsumersView";
import MemosView from "./MemosView";
import ConnectionsView from "./ConnectionsView";
import RoomBoundaryView from "../components/BoundaryRectView";
import Floor from "../models/Floor";
import { RenderTarget, useRenderTarget } from "../components/renderPrimitives/renderTarget";
import { RDiv } from "../components/renderPrimitives/RDiv";

const logger = new Logger();

export default function RoomView(props: { room: Room; mainPage: Page; print?: PagePrintInfo }) {
  const { room, mainPage, print } = props;

  const roomPage = room.getOrCreatePage();
  const roomtoFloorDrawingTransform = room.roomtoFloorDrawingTransform;

  const thisElement = useRef<HTMLDivElement>(null);

  const isMoving = useRef<boolean>(false);
  const ignoreClick = useRef<boolean>(false);
  const redraw = useRedraw();
  const renderTarget = useRenderTarget();

  const initialPoint = useRef<Point | undefined>(undefined);

  useOnChangeWatcher(state.settings);

  useOnChangeWatcher(mainPage);

  useOnChangeWatcher(room, (changedProperty, changedObject) => {
    if (changedProperty == "selected") {
      if (room.selected && state.activePaneId == (room.parent as Floor)?.page?.id) {
        bringElementToTop(thisElement.current);
      }
    }
    return true;
  });

  logger.log(`render room ${room.tag}`);

  let selectFrameClassName = "selectFrame"; // no itx
  if (print) {
    selectFrameClassName += "Print"; // no itx
  }
  if (room.selected) {
    selectFrameClassName += " selected"; // no itx
  } else if (state.settings.showIcons) {
    //selectFrameClassName = "zoomOnHover " + selectFrameClassName;
  }

  const frameWidth = mainPage.frameWidth;

  const position = roomtoFloorDrawingTransform?.position || new Point(0, 0);
  const scale = roomtoFloorDrawingTransform?.scale || 1;

  const { width, height } = roomPage.contentAreaInPixel;

  const styleRoomPage: any = {
    position: "absolute",
    // outline: room.selected ? "1px solid orange" : undefined, // no itx
    width: `${width}px`,
    height: `${height}px`,
    pointerEvents: "none",
  };

  function reassignDrawingItemsToRooms() {
    const page = room.getOrCreatePage();

    //
    // find all items that are no longer inside der bounding rect of the room
    //
    if (!room.isFloorGeneralRoom) {
      page.getDrawingItems().forEach((item) => {
        const pFloor = item.positionInFloorSpace;
        if (pFloor && !pointInBounderyRect(room.boundingRectInFloorSpace, pFloor)) {
          const newRoom = mainPage.getRoomFromPointInBoundingReact(pFloor);
          if (newRoom) {
            item.moveToRoom(newRoom);
          }
        }
      });
    }

    //
    // Assign Items of the  floor to room
    //

    const floor = room.parent as Floor;
    const roomFloorGeneral = floor?.getRoomGeneral();

    if (roomFloorGeneral) {
      const drawingItems = roomFloorGeneral.getOrCreatePage().getDrawingItems();
      drawingItems.forEach((item) => {
        if (pointInBounderyRect(room.boundingRectInFloorSpace, item.position)) {
          item.moveToRoom(room);
        }
      });
    }
  }

  function onClick() {
    state.selectionManager.selectedObject = room;
    bringElementToTop(thisElement.current);
  }

  function onChange(newBoundaryRect: any) {
    room.getOrCreatePage().freezeFloorPosition();
    room.boundingRectInFloorSpace = newBoundaryRect.copy();
    room.roomtoFloorDrawingTransform?.sizeAndPositionFromBoundingRectWithBorder(
      newBoundaryRect,
      room.getOrCreatePage(),
    );
    room.getOrCreatePage().unfreezeFloorPosition();

    reassignDrawingItemsToRooms();

    room.raiseOnChange("boundingRectInFloorSpace");
    redraw();
  }

  function RoomHeader(props: {}) {
    const w = 120;
    const h = 24;

    const t = room.boundingRectInFloorSpace.max.y - h;

    const txt = room.tag + " " + room.name;
    const measuredWidth = measureText(txt, "16px").width + 10;

    const l = room.boundingRectInFloorSpace.max.x - measuredWidth;

    const color = room.selected ? "orange" : "var(--roomInFloorBorder)";

    return (
      <RDiv
        className="roomTagAndName"
        style={{
          position: "absolute",
          left: l + "px",
          top: t + "px",
          width: measuredWidth + "px",
          height: h + "px",
          fontSize: "16px",

          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          whiteSpace: "nowrap",
          borderLeft: `1px solid ${color}`,
          borderTop: `1px solid ${color}`,
          color: color,
          textOverflow: "ellipsis",
        }}
        onClick={(ev) => {
          ev.stopPropagation();
          state.selectionManager.selectedObject = room;
          room.treeViewModel.handleOpen();
        }}
        onDoubleClick={(ev) => {
          ev.stopPropagation();
          room.treeViewModel.handleOpen();
        }}
      >
        {txt}
      </RDiv>
    );
  }

  const showFull = mainPage?.editRooms || room.selected;

  const forPdf = renderTarget === RenderTarget.pdf;

  return (
    <RDiv
      ref={thisElement}
      className="roomView"
      style={{
        position: "absolute", //  className="roomView", duplicate style for PDF export
        left: "0px",
        top: "0px",
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
      id={room.id}
    >
      {!forPdf && room.boundingRectInFloorSpace && (
        <RoomBoundaryView
          room={room}
          page={mainPage}
          currentZoom={mainPage.currentZoom}
          onChange={onChange}
          onClick={onClick}
          onDoubleClick={() => {
            room.treeViewModel.handleOpen();
          }}
          onRedraw={redraw}
          selected={room.selected}
        />
      )}

      {!forPdf && showFull && <RoomHeader />}

      <RDiv id="pageContent2">
        {roomPage && (
          <ConnectionsView
            mainPage={mainPage}
            page={roomPage}
            fontScale={state.settings.getConsumerScale(true)}
            connections={roomPage.connections}
            print={print}
          />
        )}
      </RDiv>

      <RDiv
        style={{
          transform: `scale(${scale})`,
          position: "absolute",
          left: position.x + "px",
          top: position.y + "px",
          pointerEvents: "none",
        }}
      >
        <RDiv id="RoomPage" className={`room`} style={styleRoomPage}>
          {room.page && <ConsumersView page={room.page} mainPage={mainPage} print={print} />}
          {room.page && <MemosView page={room.page} mainPage={mainPage} print={print} />}
        </RDiv>
      </RDiv>
    </RDiv>
  );
}
